import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Grid,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Divider,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Autocomplete,
} from "@mui/material";
import {
  Search as SearchIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Restaurant as RestaurantIcon,
  LocalDining as LocalDiningIcon,
  Public as PublicIcon,
  Person as PersonIcon,
  Info as InfoIcon,
  Flag as FlagIcon,
  Add as AddIcon,
  CloudUpload as UploadIcon,
  ImageSearch as ImageSearchIcon,
  Language as LanguageIcon,
  AutoAwesome as AutoAwesomeIcon,
} from "@mui/icons-material";
import api from "./api";
import countriesData from "./constants/countries.json";
import TagSelector from "./components/TagSelector";
import { AVAILABLE_LANGUAGES, DEFAULT_LANGUAGE } from "./utils/languages";

function RecipeManager() {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recipeToDelete, setRecipeToDelete] = useState(null);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [editIngredients, setEditIngredients] = useState([]);
  const [foodSuggestions, setFoodSuggestions] = useState([]);
  const [ingredientSearchLoading, setIngredientSearchLoading] = useState({});
  const [ingredientInputValues, setIngredientInputValues] = useState({});
  const [uploadingImage, setUploadingImage] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [selectedRecipeTags, setSelectedRecipeTags] = useState([]);
  const [editingRecipeTags, setEditingRecipeTags] = useState([]);
  const [currentLanguage, setCurrentLanguage] = useState(DEFAULT_LANGUAGE);
  const [editRecipeLanguage, setEditRecipeLanguage] = useState(DEFAULT_LANGUAGE);
  const [editIngredientErrors, setEditIngredientErrors] = useState({});
  const [newFoodDialog, setNewFoodDialog] = useState(false);
  const [newFoodData, setNewFoodData] = useState({ name: '', category: '' });
  const [categories, setCategories] = useState([]);
  const [creatingFood, setCreatingFood] = useState(false);
  
  // Debounce refs for edit form
  const editDebounceTimeoutRef = useRef(null);
  const limit = 12;
  // Always include global recipes in admin panel
  const includeGlobal = true;

  // Get auth token from localStorage
  const getAuthToken = () => {
    const token = localStorage.getItem("authToken");
    return token;
  };

  // Helper function to get contrast color for tag display
  const getContrastColor = (hex) => {
    if (!hex) return '#000000';
    // Convert hex to RGB
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    
    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
  };

  // Helper function to get language display name
  const getLanguageDisplayName = (lang) => {
    const languageNames = {
      english: 'English',
      german: 'Deutsch',
      dutch: 'Nederlands',
      french: 'Français',
      hindi: 'हिन्दी',
      chinese: '中文',
      russian: 'Русский',
      japanese: '日本語',
      italian: 'Italiano',
      arabic: 'العربية',
      spanish: 'Español'
    };
    return languageNames[lang] || lang.charAt(0).toUpperCase() + lang.slice(1);
  };

  // Helper function to get localized recipe title
  const getLocalizedRecipeTitle = (recipe) => {
    if (recipe.languageSpec && recipe.languageSpec[currentLanguage] && recipe.languageSpec[currentLanguage].title) {
      return recipe.languageSpec[currentLanguage].title;
    }
    return recipe.title || 'Unnamed Recipe';
  };

  // Helper function to get localized recipe summary
  const getLocalizedRecipeSummary = (recipe) => {
    if (recipe.languageSpec && recipe.languageSpec[currentLanguage] && recipe.languageSpec[currentLanguage].summary) {
      return recipe.languageSpec[currentLanguage].summary;
    }
    return recipe.summary || 'No description available';
  };

  // Helper function to get localized ingredient name
  const getLocalizedIngredientName = (ingredient) => {
    // If ingredient has localized food data
    if (ingredient.food && ingredient.food.languageSpec && ingredient.food.languageSpec[currentLanguage]) {
      return ingredient.food.languageSpec[currentLanguage].name || ingredient.name;
    }
    // If ingredient has direct languageSpec
    if (ingredient.languageSpec && ingredient.languageSpec[currentLanguage]) {
      return ingredient.languageSpec[currentLanguage].name || ingredient.name;
    }
    return ingredient.name || 'Unknown ingredient';
  };

  // Load all global recipes
  const loadGlobalRecipes = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await api.get("/recipes/public-admin/global-recipes", {
        params: {
          page: page,
          limit: limit,
          language: currentLanguage,
        },
      });

      if (response.data.success) {
        setRecipes(response.data.data.recipes || response.data.data);
        setTotalCount(response.data.data.totalCount || response.data.data.length);
        setHasMore(response.data.data.hasMore || false);
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load global recipes");
    } finally {
      setLoading(false);
    }
  };

  // Search recipes (using public endpoint)
  const searchRecipes = async () => {
    if (!searchQuery.trim()) {
      loadGlobalRecipes();
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await api.get("/recipes/public-search", {
        params: {
          query: searchQuery,
          page: page,
          limit: limit,
          language: currentLanguage,
        },
      });

      if (response.data.success) {
        setRecipes(response.data.data.recipes);
        setTotalCount(response.data.data.totalCount);
        setHasMore(response.data.data.hasMore);
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to search recipes");
    } finally {
      setLoading(false);
    }
  };


  // Get recipe details (using public endpoint)
  const getRecipeDetails = async (recipeId) => {
    try {
      const response = await api.get(`/recipes/public-recipe/${recipeId}`, {
        params: {
          language: currentLanguage,
        },
      });

      if (response.data.success) {
        const recipe = response.data.data;
        setSelectedRecipe(recipe);
        
        // Fetch tags for this recipe
        try {
          const tagsResponse = await api.get(`/tags/recipe/${recipeId}`);
          if (tagsResponse.data.success) {
            setSelectedRecipeTags(tagsResponse.data.data || []);
          }
        } catch (tagError) {
          console.error('Error fetching recipe tags:', tagError);
          setSelectedRecipeTags([]);
        }
        
        setDetailsDialogOpen(true);
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to fetch recipe details");
    }
  };

  // Delete recipe (using public endpoint)
  const deleteRecipe = async () => {
    if (!recipeToDelete) return;

    setLoading(true);
    try {
      await api.delete(`/recipes/public-admin/recipe/${recipeToDelete.id}`);

      // Refresh the list
      if (searchQuery.trim()) {
        searchRecipes();
      } else {
        loadGlobalRecipes();
      }
      
      setDeleteDialogOpen(false);
      setRecipeToDelete(null);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete recipe");
    } finally {
      setLoading(false);
    }
  };

  // Handle food search for edit ingredients with debounce
  const handleEditFoodSearch = useCallback((query, index) => {
    // Clear previous timeout
    if (editDebounceTimeoutRef.current) {
      clearTimeout(editDebounceTimeoutRef.current);
    }

    // If query is less than 3 characters, clear suggestions
    if (query.length < 3) {
      setFoodSuggestions([]);
      setIngredientSearchLoading(prev => ({ ...prev, [index]: false }));
      return;
    }

    // Set loading state for this specific ingredient
    setIngredientSearchLoading(prev => ({ ...prev, [index]: true }));

    // Set new timeout for debounce (500ms delay)
    editDebounceTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await api.get("/foods/generic-autocomplete", {
          params: { 
            query,
            language: editRecipeLanguage 
          },
        });
        setFoodSuggestions(response.data.data || []);
      } catch (err) {
        console.error("Error searching foods:", err);
        setFoodSuggestions([]);
      } finally {
        setIngredientSearchLoading(prev => ({ ...prev, [index]: false }));
      }
    }, 500);
  }, [editRecipeLanguage]);

  // Handle input value change for ingredient name
  const handleIngredientInputChange = (index, value) => {
    setIngredientInputValues(prev => ({ ...prev, [index]: value }));
    handleEditFoodSearch(value, index);
  };

  // Handle selecting a food for edit ingredient
  const handleEditSelectFood = async (food, index) => {
    // If food is null (cross button clicked), clear the ingredient
    if (!food) {
      const newIngredients = [...editIngredients];
      newIngredients[index] = {
        ...newIngredients[index], // Preserve existing properties including ID
        foodId: null,
        name: "",
        amount: 100,
        unit: "g",
        original: "",
        calories: 0,
        protein: 0,
        fat: 0,
        carbs: 0,
        glycemicIndex: 0,
        basePer100g: {
          calories: 0,
          protein: 0,
          fat: 0,
          carbs: 0,
        },
      };
      setEditIngredients(newIngredients);
      
      // Clear any errors for this ingredient
      setEditIngredientErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
      
      // Set input value to empty string for user to type new search
      setIngredientInputValues(prev => ({ ...prev, [index]: "" }));
      return;
    }

    try {
      const response = await api.get(`/foods/food/${food.id}`, {
        params: {
          language: editRecipeLanguage
        }
      });

      if (response.data.success) {
        const foodData = response.data.data;
        
        // Clear any errors for this ingredient
        setEditIngredientErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[index];
          return newErrors;
        });
        
        const newIngredients = [...editIngredients];
        const unitType = foodData.unitType
        const amount = foodData.unitValue

        newIngredients[index] = {
          ...newIngredients[index], // Preserve existing properties including ID
          foodId: foodData.id,
          name: foodData.name,
          amount: amount,
          unit: unitType,
          original: `${amount} ${unitType} ${foodData.name}`,
          calories: parseFloat(foodData.calories) || 0,
          protein: parseFloat(foodData.protein) || 0,
          fat: parseFloat(foodData.fat) || 0,
          carbs: parseFloat(foodData.carbs) || 0,
          glycemicIndex: parseFloat(foodData.glycemicIndex) || 0,
          basePer100g: {
            calories: parseFloat(foodData.calories) || 0,
            protein: parseFloat(foodData.protein) || 0,
            fat: parseFloat(foodData.fat) || 0,
            carbs: parseFloat(foodData.carbs) || 0,
          },
        };
        setEditIngredients(newIngredients);
        // Clear the input value since food is now selected
        setIngredientInputValues(prev => {
          const newValues = { ...prev };
          delete newValues[index];
          return newValues;
        });
      }
    } catch (err) {
      console.error("Error fetching food details:", err);
    }
  };

  // Handle adding new edit ingredient
  const handleAddEditIngredient = () => {
    const newIndex = editIngredients.length;
    const uniqueId = Date.now() + Math.random(); // Create unique ID
    setEditIngredients([
      ...editIngredients,
      {
        id: uniqueId,
        name: "",
        amount: 100,
        unit: "g",
        calories: 0,
        protein: 0,
        fat: 0,
        carbs: 0,
        glycemicIndex: 0,
      },
    ]);
    setIngredientInputValues(prev => ({ ...prev, [newIndex]: "" }));
  };

  // Handle removing edit ingredient
  const handleRemoveEditIngredient = (index) => {
    const newIngredients = [...editIngredients];
    newIngredients.splice(index, 1);
    setEditIngredients(newIngredients);
    
    // Clean up and reindex state for removed ingredient
    setIngredientInputValues(prev => {
      const newValues = {};
      Object.keys(prev).forEach(key => {
        const keyIndex = parseInt(key);
        if (keyIndex < index) {
          newValues[key] = prev[key];
        } else if (keyIndex > index) {
          newValues[keyIndex - 1] = prev[key];
        }
        // Skip the ingredient at the removed index
      });
      return newValues;
    });
    
    setIngredientSearchLoading(prev => {
      const newLoading = {};
      Object.keys(prev).forEach(key => {
        const keyIndex = parseInt(key);
        if (keyIndex < index) {
          newLoading[key] = prev[key];
        } else if (keyIndex > index) {
          newLoading[keyIndex - 1] = prev[key];
        }
        // Skip the ingredient at the removed index
      });
      return newLoading;
    });
    
    // Remove errors for this ingredient and reindex remaining errors
    setEditIngredientErrors(prev => {
      const newErrors = {};
      Object.keys(prev).forEach(key => {
        const keyIndex = parseInt(key);
        if (keyIndex < index) {
          newErrors[key] = prev[key];
        } else if (keyIndex > index) {
          newErrors[keyIndex - 1] = prev[key];
        }
      });
      return newErrors;
    });
  };

  // Fetch categories for food creation
  const fetchCategories = async () => {
    try {
      const response = await api.get('/category/categories');
      if (response.data.success) {
        setCategories(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  // Handle creating new food
  const handleCreateNewFood = async () => {
    if (!newFoodData.name.trim() || !newFoodData.category) {
      setError("Please enter food name and select category");
      return;
    }

    setCreatingFood(true);
    try {
      // First, try to get AI nutritional data
      const aiResponse = await api.post('/foods/fill-with-ai', {
        name: newFoodData.name,
        category: newFoodData.category,
        generateImage: true,
        languages: [editRecipeLanguage]
      });

      let foodData = {
        name: newFoodData.name,
        category: newFoodData.category,
        isCustom: true,
        calories: 0,
        protein: 0,
        fat: 0,
        carbs: 0,
        glycemicIndex: 50,
        languageSpec: {
          [editRecipeLanguage]: { name: newFoodData.name, customImageUrl: '' }
        }
      };

      // If AI data is available, use it
      if (aiResponse.data.success) {
        const aiData = aiResponse.data.data;
        foodData = {
          ...foodData,
          calories: aiData.calories || 0,
          protein: aiData.protein || 0,
          fat: aiData.fat || 0,
          carbs: aiData.carbs || 0,
          glycemicIndex: aiData.glycemicIndex || 50,
          image: aiData.generatedImage || '',
          languageSpec: {
            ...foodData.languageSpec,
            ...aiData.languageSpec
          }
        };
      }

      // Create the food item
      const createResponse = await api.post('/foods', foodData);
      
      if (createResponse.data.success) {
        const createdFood = createResponse.data.data;
        
        // Add as ingredient to current recipe
        const newIngredients = [...editIngredients];
        const newIndex = newIngredients.length;
        const uniqueId = Date.now() + Math.random();
        newIngredients.push({
          id: uniqueId,
          foodId: createdFood.id,
          name: createdFood.name,
          amount: 100,
          unit: "g",
          original: `100 g ${createdFood.name}`,
          calories: parseFloat(createdFood.calories) || 0,
          protein: parseFloat(createdFood.protein) || 0,
          fat: parseFloat(createdFood.fat) || 0,
          carbs: parseFloat(createdFood.carbs) || 0,
          glycemicIndex: parseFloat(createdFood.glycemicIndex) || 0,
          basePer100g: {
            calories: parseFloat(createdFood.calories) || 0,
            protein: parseFloat(createdFood.protein) || 0,
            fat: parseFloat(createdFood.fat) || 0,
            carbs: parseFloat(createdFood.carbs) || 0,
          },
        });
        setEditIngredients(newIngredients);
        // Don't set input value since food is already selected
        
        // setSuccess(`Food "${createdFood.name}" created and added to recipe!`);
        setNewFoodDialog(false);
        setNewFoodData({ name: '', category: '' });
      }
    } catch (error) {
      console.error('Failed to create food:', error);
      setError('Failed to create food item. Please try again.');
    } finally {
      setCreatingFood(false);
    }
  };

  // Handle updating edit ingredient amount
  const handleUpdateEditIngredientAmount = (index, amount) => {
    const newIngredients = [...editIngredients];
    const ingredient = newIngredients[index];
    const numericAmount = parseFloat(amount) || 0;

    ingredient.amount = numericAmount;

    if (ingredient.foodId && numericAmount > 0 && ingredient.basePer100g) {
      const factor = numericAmount / 100;
      ingredient.calories = ingredient.basePer100g.calories * factor;
      ingredient.protein = ingredient.basePer100g.protein * factor;
      ingredient.fat = ingredient.basePer100g.fat * factor;
      ingredient.carbs = ingredient.basePer100g.carbs * factor;
      ingredient.original = `${numericAmount} ${ingredient.unit} ${ingredient.name}`;
    }

    setEditIngredients(newIngredients);
  };

  // Handle AI image generation for edit
  const handleEditGenerateImage = async () => {
    if (!editFormData.title?.trim()) {
      setError("Please enter a recipe title to generate an image");
      return;
    }

    setGeneratingImage(true);
    setError("");

    try {
      const response = await api.post("/foods/generate-image", {
        name: editFormData.title,
        category: null,
        customPrompt: `recipe: ${editFormData.title}${editFormData.summary ? `, ${editFormData.summary}` : ''}, professional food photography style`
      });

      // Check if response exists and has expected structure
      if (response && response.data) {
        if (response.data.success && response.data.data && response.data.data.imageUrl) {
          setEditFormData({ ...editFormData, imageUrl: response.data.data.imageUrl });
        } else {
          // Handle API success=false or missing data
          const errorMessage = response.data.error || response.data.message || "Failed to generate AI image. Please try again.";
          setError(errorMessage);
        }
      } else {
        setError("Invalid response from server. Please try again.");
      }
    } catch (err) {
      console.error("AI image generation error:", err);
      
      // Comprehensive error handling
      let errorMessage = "Failed to generate AI image. Please try again.";
      
      if (err?.response) {
        // Server responded with error status
        if (err.response.status === 401) {
          errorMessage = "Authentication required. Please log in again.";
        } else if (err.response.status === 403) {
          errorMessage = "You don't have permission to generate images.";
        } else if (err.response.status === 429) {
          errorMessage = "Too many requests. Please wait a moment and try again.";
        } else if (err.response.status >= 500) {
          errorMessage = "Server error. Please try again later.";
        } else if (err.response.data) {
          // Try to extract error message from response
          errorMessage = err.response.data.error || 
                        err.response.data.message || 
                        err.response.data.details || 
                        `Server error (${err.response.status}). Please try again.`;
        }
      } else if (err?.request) {
        // Network error
        errorMessage = "Network error. Please check your connection and try again.";
      } else if (err?.message) {
        // Other errors
        errorMessage = `Error: ${err.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setGeneratingImage(false);
    }
  };

  // Handle edit image upload
  const handleEditImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    const formData = new FormData();
    formData.append("image", file);

    try {
      const response = await api.post("/upload-image/public-admin/upload-recipe-image", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        setEditFormData({ ...editFormData, imageUrl: response.data.url });
      }
    } catch (err) {
      // Fallback to base64 if upload fails
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditFormData({ ...editFormData, image: reader.result });
      };
      reader.readAsDataURL(file);
    } finally {
      setUploadingImage(false);
    }
  };

  // Get localized title for edit form
  const getEditLocalizedTitle = (recipe, language) => {
    if (recipe.languageSpec && recipe.languageSpec[language] && recipe.languageSpec[language].title) {
      return recipe.languageSpec[language].title;
    }
    return recipe.title || '';
  };

  // Get localized summary for edit form
  const getEditLocalizedSummary = (recipe, language) => {
    if (recipe.languageSpec && recipe.languageSpec[language] && recipe.languageSpec[language].summary) {
      return recipe.languageSpec[language].summary;
    }
    return recipe.summary || '';
  };

  // Update edit form data when language changes
  const updateEditFormForLanguage = (recipe, language) => {
    if (!recipe) return;
    
    setEditFormData(prev => ({
      ...prev,
      title: getEditLocalizedTitle(recipe, language),
      summary: getEditLocalizedSummary(recipe, language)
    }));
  };

  // Edit recipe
  const handleEditRecipe = async (recipe) => {
    setEditingRecipe(recipe);
    setEditRecipeLanguage(currentLanguage);
    setEditFormData({
      title: getEditLocalizedTitle(recipe, currentLanguage),
      summary: getEditLocalizedSummary(recipe, currentLanguage),
      servings: recipe.servings || 1,
      unitType: recipe.unitType || 'gram',
      unitValue: recipe.unitValue || 100,
      countryOfOrigin: recipe.countryOfOrigin || '',
      image: recipe.image || '',
      imageUrl: ''
    });
    
    // Get full recipe details including ingredients and tags
    try {
      const response = await api.get(`/recipes/public-recipe/${recipe.id}`);
      if (response.data.success) {
        const fullRecipe = response.data.data;
        const ingredients = (fullRecipe.extendedIngredients || []).map((ingredient, index) => ({
          ...ingredient,
          id: ingredient.id || Date.now() + Math.random() + index // Ensure unique ID
        }));
        setEditIngredients(ingredients);
        
        // Initialize input values for each ingredient (only for ingredients without foodId)
        const inputValues = {};
        ingredients.forEach((ingredient, index) => {
          if (!ingredient.foodId) {
            inputValues[index] = ingredient.name || "";
          }
          // For ingredients with foodId, don't set input value - let the component show the ingredient name
        });
        setIngredientInputValues(inputValues);
      }
      
      // Fetch recipe tags
      try {
        const tagsResponse = await api.get(`/tags/recipe/${recipe.id}`);
        if (tagsResponse.data.success) {
          const tags = tagsResponse.data.data || [];
          setEditingRecipeTags(tags.map(tag => tag.id));
        }
      } catch (tagError) {
        console.error('Error fetching recipe tags:', tagError);
        setEditingRecipeTags([]);
      }
    } catch (err) {
      console.error("Error fetching recipe details:", err);
      setEditIngredients([]);
      setIngredientInputValues({});
      setEditingRecipeTags([]);
    }
    
    setEditIngredientErrors({});
    setEditDialogOpen(true);
  };

  // Calculate total nutrition for edit
  const calculateEditTotalNutrition = () => {
    const totals = editIngredients.reduce(
      (acc, ing) => ({
        calories: acc.calories + (parseFloat(ing.calories) || 0),
        protein: acc.protein + (parseFloat(ing.protein) || 0),
        fat: acc.fat + (parseFloat(ing.fat) || 0),
        carbs: acc.carbs + (parseFloat(ing.carbs) || 0),
      }),
      { calories: 0, protein: 0, fat: 0, carbs: 0 }
    );
    console.log("🤔 ~ :394 ~ calculateEditTotalNutrition ~ totals:", totals)
    return totals;
  };

  // Validate edit ingredients
  const validateEditIngredients = () => {
    const errors = {};
    let hasValidIngredient = false;
    
    editIngredients.forEach((ingredient, index) => {
      const ingredientErrors = [];
      
      if (!ingredient.name || ingredient.name.trim() === '') {
        ingredientErrors.push('Ingredient name is required');
      }
      
      if (!ingredient.foodId) {
        ingredientErrors.push('Please select a valid food item');
      }
      
      if (ingredientErrors.length > 0) {
        errors[index] = ingredientErrors;
      } else {
        hasValidIngredient = true;
      }
    });
    
    setEditIngredientErrors(errors);
    return { isValid: Object.keys(errors).length === 0, hasValidIngredient };
  };

  // Update recipe
  const updateRecipe = async () => {
    if (!editingRecipe || !editFormData.title || editIngredients.length === 0 || !editFormData.countryOfOrigin) {
      setError("Please fill in recipe title, select country of origin, and add at least one ingredient");
      return;
    }

    // Validate ingredients
    const { isValid, hasValidIngredient } = validateEditIngredients();
    
    if (!isValid) {
      setError("Please fix ingredient errors. Each ingredient must have a name and be selected from the search.");
      return;
    }

    setLoading(true);
    try {
      const nutrition = calculateEditTotalNutrition();
      
      const updateData = {
        ...editFormData,
        extendedIngredients: editIngredients.map((ingredient) => ({
            foodId: ingredient.foodId || null,
            name: ingredient.name,
            amount: ingredient.amount,
            unit: ingredient.unit,
            original: ingredient.original || `${ingredient.amount} ${ingredient.unit} ${ingredient.name}`.trim(),
            calories: ingredient.calories || 0,
            protein: ingredient.protein || 0,
            fat: ingredient.fat || 0,
            carbs: ingredient.carbs || 0,
            glycemicIndex: ingredient.glycemicIndex || 0,
          })),
        nutrition: {
          nutrients: [
            { name: "Calories", amount: nutrition.calories },
            { name: "Protein", amount: nutrition.protein },
            { name: "Fat", amount: nutrition.fat },
            { name: "Carbohydrates", amount: nutrition.carbs },
          ],
        },
      };
      
      const response = await api.put(`/recipes/public-admin/recipe/${editingRecipe.id}`, updateData);
      
      if (response.data.success) {
        // Update recipe tags
        try {
          await api.post(`/tags/public-admin/recipe/${editingRecipe.id}`, {
            tagIds: editingRecipeTags
          });
        } catch (tagError) {
          console.error('Error updating recipe tags:', tagError);
          // Don't fail the whole operation for tag errors
        }
        
        // Refresh the list
        if (searchQuery.trim()) {
          searchRecipes();
        } else {
          loadGlobalRecipes();
        }
        
        setEditDialogOpen(false);
        setEditingRecipe(null);
        setEditFormData({});
        setEditIngredients([]);
        setIngredientInputValues({});
        setIngredientSearchLoading({});
        setEditingRecipeTags([]);
        setEditRecipeLanguage(currentLanguage);
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update recipe");
    } finally {
      setLoading(false);
    }
  };

  // Handle search on Enter key
  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      setPage(0);
      searchRecipes();
    }
  };

  // Handle page change
  const handlePageChange = (event, value) => {
    setPage(value - 1);
  };

  // Load recipes on component mount
  useEffect(() => {
    loadGlobalRecipes();
  }, []);

  // Fetch categories when new food dialog opens
  useEffect(() => {
    if (newFoodDialog) {
      fetchCategories();
    }
  }, [newFoodDialog]);

  // Load recipes on page or language change
  useEffect(() => {
    if (searchQuery.trim()) {
      searchRecipes();
    } else {
      loadGlobalRecipes();
    }
  }, [page, currentLanguage]);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (editDebounceTimeoutRef.current) {
        clearTimeout(editDebounceTimeoutRef.current);
      }
    };
  }, []);

  const pageCount = Math.ceil(totalCount / limit);

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
        <RestaurantIcon />
        Global Recipe Manager
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Search and Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mb: 3 }}>
          🎛️ Filters & Language
        </Typography>
        
        {/* Language Selection */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <LanguageIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="subtitle2" color="text.primary">
              Select Language:
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {AVAILABLE_LANGUAGES.map(lang => (
              <Chip
                key={lang}
                label={getLanguageDisplayName(lang)}
                onClick={() => setCurrentLanguage(lang)}
                color={currentLanguage === lang ? 'primary' : 'default'}
                variant={currentLanguage === lang ? 'filled' : 'outlined'}
                size="medium"
                sx={{
                  fontWeight: currentLanguage === lang ? 'bold' : 'normal',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    transition: 'transform 0.2s'
                  }
                }}
              />
            ))}
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Search Section */}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Search Recipes"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => {
                setPage(0);
                searchRecipes();
              }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : "Search"}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Recipe Grid */}
      <Grid 
        container 
        spacing={3} 
        sx={{ 
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(1, 1fr)',  // 1 card per row on mobile
            sm: 'repeat(2, 1fr)',  // 2 cards per row on tablet
            md: 'repeat(3, 1fr)',  // 3 cards per row on desktop
          },
          gap: 3,
          '& > .MuiGrid-item': {
            paddingTop: 0,
            paddingLeft: 0
          }
        }}
      >
        {recipes.map((recipe) => (
          <Grid item key={recipe.id} sx={{ height: '100%' }}>
            <Card 
              sx={{ 
                height: 450, 
                width: "100%",
                display: "flex", 
                flexDirection: "column",
                position: "relative",
                transition: 'box-shadow 0.3s ease-in-out, transform 0.2s',
                '&:hover': {
                  boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                  transform: 'translateY(-5px)',
                }
              }}
            >
              {/* Delete button in top right corner */}
              <IconButton
                size="small"
                color="error"
                onClick={() => {
                  setRecipeToDelete(recipe);
                  setDeleteDialogOpen(true);
                }}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  zIndex: 2,
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 1)",
                  },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
              <Box
                sx={{
                  height: 180,
                  width: "100%",
                  backgroundColor: recipe.image ? "transparent" : "grey.100",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden"
                }}
              >
                {recipe.image ? (
                  <CardMedia
                    component="img"
                    height="180"
                    image={recipe.image}
                    alt={getLocalizedRecipeTitle(recipe)}
                    sx={{ 
                      objectFit: "cover",
                      width: "100%",
                      height: "100%"
                    }}
                  />
                ) : (
                  <RestaurantIcon 
                    sx={{ 
                      fontSize: 60, 
                      color: "grey.400" 
                    }} 
                  />
                )}
              </Box>
              <CardContent sx={{ flexGrow: 1, p: 2, display: "flex", flexDirection: "column" }}>
                <Typography 
                  variant="h6" 
                  component="div" 
                  sx={{ 
                    mb: 1, 
                    fontSize: "1rem",
                    fontWeight: 600,
                    lineHeight: 1.2,
                    height: 48,
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical"
                  }}
                >
                  {getLocalizedRecipeTitle(recipe)}
                </Typography>
                
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    mb: 2, 
                    height: 40,
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    fontSize: "0.875rem"
                  }}
                >
                  {getLocalizedRecipeSummary(recipe).substring(0, 80) + "..."}
                </Typography>

                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1, minHeight: 32 }}>
                  <Chip
                    icon={<LocalDiningIcon />}
                    label={`${recipe.servings || 1} servings`}
                    size="small"
                    sx={{ fontSize: "0.75rem", height: 24 }}
                  />
                  {recipe.countryOfOrigin && (
                    <Chip
                      icon={<FlagIcon />}
                      label={countriesData[recipe.countryOfOrigin] || recipe.countryOfOrigin}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ fontSize: "0.75rem", height: 24 }}
                    />
                  )}
                </Box>

                {/* Nutrition Summary */}
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: "auto", minHeight: 32 }}>
                  {recipe.calories && (
                    <Chip 
                      label={`${parseFloat(recipe.calories).toFixed(0)} cal`} 
                      size="small" 
                      color="primary" 
                      sx={{ fontSize: "0.75rem", height: 24 }}
                    />
                  )}
                  {recipe.protein && (
                    <Chip 
                      label={`${parseFloat(recipe.protein).toFixed(1)}g protein`} 
                      size="small" 
                      sx={{ fontSize: "0.75rem", height: 24 }}
                    />
                  )}
                </Box>
              </CardContent>
              <CardActions sx={{ p: 1, pt: 0, justifyContent: "space-between" }}>
                <Button
                  size="small"
                  startIcon={<InfoIcon />}
                  onClick={() => getRecipeDetails(recipe.id)}
                  sx={{ fontSize: "0.75rem" }}
                >
                  Details
                </Button>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => handleEditRecipe(recipe)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {recipes.length === 0 && !loading && (
        <Paper sx={{ p: 4, textAlign: "center", mt: 4 }}>
          <Typography variant="h6" color="text.secondary">
            {searchQuery ? "No recipes found. Try a different search term." : "No global recipes found."}
          </Typography>
        </Paper>
      )}

      {/* Pagination */}
      {pageCount > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Pagination
            count={pageCount}
            page={page + 1}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}

      {/* Recipe Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => {
          setDetailsDialogOpen(false);
          setSelectedRecipeTags([]);
        }}
        maxWidth="md"
        fullWidth
      >
        {selectedRecipe && (
          <>
            <DialogTitle>
              {getLocalizedRecipeTitle(selectedRecipe)}
            </DialogTitle>
            <DialogContent>
              {selectedRecipe.image && (
                <Box sx={{ mb: 2, textAlign: "center" }}>
                  <img
                    src={selectedRecipe.image}
                    alt={getLocalizedRecipeTitle(selectedRecipe)}
                    style={{ maxWidth: "100%", maxHeight: 300, borderRadius: 8 }}
                  />
                </Box>
              )}
              
              {getLocalizedRecipeSummary(selectedRecipe) !== 'No description available' && (
                <Typography variant="body1" paragraph>
                  {getLocalizedRecipeSummary(selectedRecipe)}
                </Typography>
              )}

              <Box sx={{ mb: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Chip label={`Servings: ${selectedRecipe.servings || 1}`} />
                {selectedRecipe.unitType && selectedRecipe.unitValue && (
                  <Chip 
                    label={`Unit: ${selectedRecipe.unitValue} ${selectedRecipe.unitType}`} 
                    color="secondary" 
                  />
                )}
                {selectedRecipe.countryOfOrigin && (
                  <Chip 
                    icon={<FlagIcon />}
                    label={`Origin: ${countriesData[selectedRecipe.countryOfOrigin] || selectedRecipe.countryOfOrigin}`}
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Box>

              {/* Recipe Tags */}
              {selectedRecipeTags.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Tags:
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {selectedRecipeTags.map((tag) => (
                      <Chip
                        key={tag.id}
                        label={`${tag.emoji} ${tag.name}`}
                        size="small"
                        sx={{
                          backgroundColor: tag.color,
                          color: getContrastColor(tag.color),
                          fontWeight: 'bold'
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              <Typography variant="h6" gutterBottom>
                Ingredients ({selectedRecipe.extendedIngredients?.length || 0})
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {selectedRecipe.extendedIngredients?.map((ingredient, index) => (
                <Box key={index} sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    • {ingredient.original || `${ingredient.amount} ${ingredient.unit} ${ingredient.name}`}
                  </Typography>
                </Box>
              ))}

              {selectedRecipe.nutrition && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    Nutrition Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    {selectedRecipe.nutrition.nutrients?.map((nutrient) => (
                      <Grid item xs={6} sm={3} key={nutrient.name}>
                        <Paper sx={{ p: 2, textAlign: "center" }}>
                          <Typography variant="h6">{Number(nutrient.amount).toFixed(1)}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {nutrient.name} {nutrient.unit || ""}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Edit Recipe Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setEditingRecipeTags([]);
          setEditRecipeLanguage(currentLanguage);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Edit Recipe</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* Language Selection */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <LanguageIcon />
                Recipe Language
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {AVAILABLE_LANGUAGES.map(lang => (
                  <Chip
                    key={lang}
                    label={getLanguageDisplayName(lang)}
                    onClick={() => {
                      setEditRecipeLanguage(lang);
                      updateEditFormForLanguage(editingRecipe, lang);
                    }}
                    color={editRecipeLanguage === lang ? 'primary' : 'default'}
                    variant={editRecipeLanguage === lang ? 'filled' : 'outlined'}
                    size="medium"
                    sx={{
                      fontWeight: editRecipeLanguage === lang ? 'bold' : 'normal',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        transition: 'transform 0.2s'
                      }
                    }}
                  />
                ))}
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Select the language for ingredient search and recipe content. Title and summary will update based on available translations.
              </Typography>
            </Paper>
            
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Recipe Information
                    </Typography>
                    <TextField
                      fullWidth
                      label="Recipe Title"
                      value={editFormData.title || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                      sx={{ mb: 2 }}
                      required
                    />
                    <TextField
                      fullWidth
                      label="Summary"
                      value={editFormData.summary || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, summary: e.target.value })}
                      multiline
                      rows={3}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      label="Servings"
                      type="number"
                      value={editFormData.servings || 1}
                      onChange={(e) => setEditFormData({ ...editFormData, servings: parseInt(e.target.value) || 1 })}
                      InputProps={{ inputProps: { min: 1 } }}
                      sx={{ mb: 2 }}
                    />
                    
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Unit Type</InputLabel>
                      <Select
                        value={editFormData.unitType || 'gram'}
                        label="Unit Type"
                        onChange={(e) => setEditFormData({ ...editFormData, unitType: e.target.value })}
                      >
                        <MenuItem value="gram">Gram</MenuItem>
                        <MenuItem value="ml">Milliliter</MenuItem>
                        <MenuItem value="piece_portion">Piece/Portion</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <TextField
                      fullWidth
                      label="Unit Value"
                      type="number"
                      value={editFormData.unitValue || 100}
                      onChange={(e) => setEditFormData({ ...editFormData, unitValue: parseFloat(e.target.value) || 100 })}
                      InputProps={{ inputProps: { min: 1 } }}
                      sx={{ mb: 2 }}
                    />
                    
                    <FormControl fullWidth sx={{ mb: 2 }} required>
                      <InputLabel>Country of Origin</InputLabel>
                      <Select
                        value={editFormData.countryOfOrigin || ''}
                        label="Country of Origin"
                        onChange={(e) => setEditFormData({ ...editFormData, countryOfOrigin: e.target.value })}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 250,
                              overflow: 'auto',
                            },
                          },
                          anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                          },
                          transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                          },
                        }}
                        renderValue={(selected) => {
                          if (!selected) return '';
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar
                                src={`/images/country/${selected.toLowerCase()}.svg`}
                                sx={{ width: 24, height: 24 }}
                              >
                                <FlagIcon fontSize="small" />
                              </Avatar>
                              {countriesData[selected]}
                            </Box>
                          );
                        }}
                      >
                        {Object.entries(countriesData).map(([code, name]) => (
                          <MenuItem key={code} value={code}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar
                                src={`/images/country/${code.toLowerCase()}.svg`}
                                sx={{ width: 24, height: 24 }}
                              >
                                <FlagIcon fontSize="small" />
                              </Avatar>
                              {name}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </CardContent>
                </Card>
              </Grid>

              {/* Image Upload */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Recipe Image
                    </Typography>
                    <Box sx={{ textAlign: "center" }}>
                      {(editFormData.imageUrl || editFormData.image) && (
                        <Box sx={{ mb: 2 }}>
                          <img
                            src={editFormData.imageUrl || editFormData.image}
                            alt="Recipe"
                            style={{
                              maxWidth: "100%",
                              maxHeight: 200,
                              borderRadius: 8,
                            }}
                          />
                        </Box>
                      )}
                      <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
                        <Button
                          variant="contained"
                          component="label"
                          startIcon={uploadingImage ? <CircularProgress size={20} /> : <UploadIcon />}
                          disabled={uploadingImage || generatingImage}
                        >
                          {uploadingImage ? "Uploading..." : "Upload Image"}
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={handleEditImageUpload}
                          />
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={generatingImage ? <CircularProgress size={20} /> : <ImageSearchIcon />}
                          onClick={handleEditGenerateImage}
                          disabled={generatingImage || uploadingImage || !editFormData.title?.trim()}
                          color="secondary"
                        >
                          {generatingImage ? "Generating..." : "AI Generate"}
                        </Button>
                      </Box>
                      {!editFormData.title?.trim() && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Enter a recipe title to enable AI image generation
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Tags */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Recipe Tags
                    </Typography>
                    <TagSelector
                      selectedTagIds={editingRecipeTags}
                      onTagsChange={setEditingRecipeTags}
                      label="Select tags for this recipe"
                      placeholder="Choose relevant tags to categorize this recipe..."
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Tags help users find and categorize recipes easily.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Ingredients */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                      <Typography variant="h6">
                        Ingredients
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          startIcon={<AutoAwesomeIcon />}
                          onClick={() => setNewFoodDialog(true)}
                          variant="outlined"
                          size="small"
                          color="secondary"
                        >
                          Create New Food
                        </Button>
                        <Button
                          startIcon={<AddIcon />}
                          onClick={handleAddEditIngredient}
                          variant="contained"
                          size="small"
                        >
                          Add Ingredient
                        </Button>
                      </Box>
                    </Box>

                    {editIngredients.map((ingredient, index) => (
                      <Paper 
                        key={ingredient.id || index} 
                        sx={{ 
                          p: 2, 
                          mb: 2,
                          border: editIngredientErrors[index] ? '2px solid' : 'none',
                          borderColor: 'error.main'
                        }} 
                        elevation={2}
                      >
                        <Grid container spacing={2} alignItems="flex-start">
                          <Grid item xs={12} md={4}>
                            <Autocomplete
                              freeSolo
                              options={foodSuggestions}
                              value={ingredient.foodId ? { name: ingredient.name, id: ingredient.foodId } : null}
                              getOptionLabel={(option) => {
                                if (typeof option === 'string') return option;
                                return option.word || option.name || "";
                              }}
                              loading={ingredientSearchLoading[index] || false}
                              onInputChange={(event, value) => {
                                if (event && event.type !== 'blur') {
                                  handleIngredientInputChange(index, value);
                                }
                              }}
                              onChange={(event, value) => {
                                if (typeof value === 'object' && value !== null) {
                                  handleEditSelectFood(value, index);
                                }
                              }}
                              inputValue={ingredient.foodId ? ingredient.name : (ingredientInputValues[index] || "")}
                              noOptionsText={
                                ingredientSearchLoading[index] 
                                  ? "Searching..." 
                                  : "Type at least 3 characters to search foods"
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Search Food"
                                  variant="outlined"
                                  error={!!editIngredientErrors[index]}
                                  helperText={
                                    editIngredientErrors[index] 
                                      ? editIngredientErrors[index].join('. ') 
                                      : "Type at least 3 characters to search"
                                  }
                                  InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                      <>
                                        {ingredientSearchLoading[index] ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                      </>
                                    ),
                                  }}
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={6} md={2}>
                            <TextField
                              fullWidth
                              label="Amount"
                              type="number"
                              value={ingredient.amount}
                              onChange={(e) => handleUpdateEditIngredientAmount(index, e.target.value)}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">{ingredient.unit}</InputAdornment>,
                              }}
                            />
                          </Grid>
                          <Grid item xs={6} md={5}>
                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                              <Chip label={`${Number(ingredient.calories).toFixed(1)} cal`} size="small" />
                              <Chip label={`${Number(ingredient.protein).toFixed(1)}g protein`} size="small" />
                              <Chip label={`${Number(ingredient.fat).toFixed(1)}g fat`} size="small" />
                              <Chip label={`${Number(ingredient.carbs).toFixed(1)}g carbs`} size="small" />
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={1}>
                            <IconButton
                              color="error"
                              onClick={() => handleRemoveEditIngredient(index)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}

                    {editIngredients.length === 0 && (
                      <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                        No ingredients added yet. Click "Add Ingredient" to start.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Total Nutrition */}
              {editIngredients.length > 0 && (() => {
                const totalNutrition = calculateEditTotalNutrition();
                return (
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Total Nutrition (per recipe)
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6} md={3}>
                            <Paper sx={{ p: 2, textAlign: "center", bgcolor: "primary.light", color: "primary.contrastText" }}>
                              <Typography variant="h4">{Number(totalNutrition.calories).toFixed(0)}</Typography>
                              <Typography>Calories</Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Paper sx={{ p: 2, textAlign: "center", bgcolor: "secondary.light", color: "secondary.contrastText" }}>
                              <Typography variant="h4">{Number(totalNutrition.protein).toFixed(1)}g</Typography>
                              <Typography>Protein</Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Paper sx={{ p: 2, textAlign: "center", bgcolor: "warning.light", color: "warning.contrastText" }}>
                              <Typography variant="h4">{Number(totalNutrition.fat).toFixed(1)}g</Typography>
                              <Typography>Fat</Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Paper sx={{ p: 2, textAlign: "center", bgcolor: "info.light", color: "info.contrastText" }}>
                              <Typography variant="h4">{Number(totalNutrition.carbs).toFixed(1)}g</Typography>
                              <Typography>Carbs</Typography>
                            </Paper>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })()}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={updateRecipe}
            variant="contained"
            disabled={loading || !editFormData.title?.trim() || editIngredients.length === 0}
          >
            {loading ? <CircularProgress size={24} /> : "Update Recipe"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{recipeToDelete?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={deleteRecipe}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create New Food Dialog */}
      <Dialog open={newFoodDialog} onClose={() => setNewFoodDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoAwesomeIcon color="secondary" />
            Create New Food Item
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create a new food item that doesn't exist in the database. AI will help fetch nutritional information.
          </Typography>
          
          <TextField
            fullWidth
            label="Food Name"
            value={newFoodData.name}
            onChange={(e) => setNewFoodData({ ...newFoodData, name: e.target.value })}
            sx={{ mb: 3 }}
            placeholder="e.g., Organic Quinoa, Fresh Salmon, etc."
          />
          
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={newFoodData.category}
              label="Category"
              onChange={(e) => setNewFoodData({ ...newFoodData, category: e.target.value })}
            >
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.name}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>AI Enhancement:</strong> After creation, the system will automatically fetch nutritional information 
            and generate an appropriate food image using AI technology.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewFoodDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateNewFood}
            variant="contained"
            disabled={creatingFood || !newFoodData.name.trim() || !newFoodData.category}
            startIcon={creatingFood ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
          >
            {creatingFood ? 'Creating with AI...' : 'Create & Add to Recipe'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default RecipeManager;